<template>
    <div
        style="position: relative;"
        :class="[{ 'required-field-placeholder-hint': placeholderHintActive }, inputStyle === 'tailwind' ? 'input-style-tailwind' : '']"
    >
        <v-text-field
            ref="textField"
            :id="inputId"
            v-model="inputValue"
            :disabled="disabled"
            :error-messages="errorMessages"
            :label="label"
            :placeholder="placeholder"
            :persistent-placeholder="persistentPlaceholder"
            :dense="dense"
            :outlined="outlined"
            :clearable="clearable"
            :loading="loading"
            @input="handleInput"
            @focus="onFocus"
            @blur="onBlur"
            @keydown="onKeydown"
            :hide-details="hideDetails"
        />
        
        <!-- Suggestions dropdown -->
        <div 
            v-if="showSuggestions && suggestions.length > 0" 
            class="suggestions-dropdown"
            role="listbox"
            aria-label="Suggestions d’adresses"
        >
            <v-list dense class="address-suggestions-list">
                <v-list-item
                    v-for="(suggestion, index) in suggestions"
                    :key="suggestion.place_id"
                    :class="{ 'v-list-item--active': index === selectedIndex }"
                    @click="selectSuggestion(suggestion)"
                    @mouseenter="selectedIndex = index"
                    class="address-suggestion"
                    role="option"
                    :aria-selected="index === selectedIndex"
                    @mousedown.prevent
                >
                    <span class="address-suggestion-icon" aria-hidden="true"><v-icon size="18">mdi-map-marker-outline</v-icon></span>
                    <v-list-item-content>
                        <v-list-item-title class="address-suggestion-title">{{ suggestionTitle(suggestion) }}</v-list-item-title>
                        <v-list-item-subtitle v-if="suggestionSubtitle(suggestion)" class="address-suggestion-subtitle">{{ suggestionSubtitle(suggestion) }}</v-list-item-subtitle>
                    </v-list-item-content>
                </v-list-item>
            </v-list>
        </div>
    </div>
</template>

<script>
export default {
    name: 'GoogleAddressAutocomplete',
    props: {
        inputId: String,
        resolveDetails: { type: Boolean, default: true },
        value: {
            type: String,
            default: ''
        },
        errorMessages: {
            type: Array,
            default: () => []
        },
        label: {
            type: String,
            default: 'Address'
        },
        placeholder: {
            type: String,
            default: 'Start typing an address...'
        },
        disabled: {
            type: Boolean,
            default: false
        },
        dense: {
            type: Boolean,
            default: false
        },
        outlined: {
            type: Boolean,
            default: false
        },
        clearable: {
            type: Boolean,
            default: true
        },
        /** 'vuetify' (default) or 'tailwind' – passed from parent (e.g. AddressForm) */
        inputStyle: {
            type: String,
            default: 'vuetify',
            validator: (v) => ['vuetify', 'tailwind'].includes(v),
        },
        hideDetails: {
            type: [Boolean, String],
            default: false
        },
        persistentPlaceholder: {
            type: Boolean,
            default: false,
        },
        placeholderHintActive: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            inputValue: this.value || '',
            suggestions: [],
            loading: false,
            autocompleteService: null,
            placesService: null,
            debounceTimer: null,
            showSuggestions: false,
            selectedIndex: -1,
            isFocused: false
        };
    },
    mounted() {
        this.initializeGoogleServices();
        
        // Fallback: try to initialize again after delays if not ready
        if (!this.autocompleteService) {
            setTimeout(() => {
                this.initializeGoogleServices();
            }, 1000);
            
            // Try again after 3 seconds
            setTimeout(() => {
                this.initializeGoogleServices();
            }, 3000);
        }
    },
    methods: {
        suggestionTitle(suggestion) {
            return suggestion.prediction?.structured_formatting?.main_text || suggestion.formatted_address.split(',')[0];
        },
        suggestionSubtitle(suggestion) {
            return suggestion.prediction?.structured_formatting?.secondary_text || suggestion.formatted_address.split(',').slice(1).join(',').trim();
        },
        initializeGoogleServices() {
            
            if (window.google && window.google.maps && window.google.maps.places) {
                this.autocompleteService = new window.google.maps.places.AutocompleteService();
                this.placesService = new window.google.maps.places.PlacesService(
                    document.createElement('div')
                );
            } else {
                console.warn('Google Maps or Places API not loaded');
            }
        },
        handleInput(value) {
            this.inputValue = value;
            this.$emit('input', value);
            
            // Déclencher la recherche si on a assez de caractères
            if (value && value.length >= 3) {
                this.performSearch(value);
            } else {
                this.suggestions = [];
                this.showSuggestions = false;
            }
        },
        onFocus() {
            this.isFocused = true;
            if (this.suggestions.length > 0) {
                this.showSuggestions = true;
            }
        },
        onBlur() {
            this.isFocused = false;
            // Délai pour permettre le clic sur les suggestions
            setTimeout(() => {
                this.showSuggestions = false;
            }, 200);
        },
        onKeydown(event) {
            if (!this.showSuggestions || this.suggestions.length === 0) return;
            
            switch (event.key) {
                case 'ArrowDown':
                    event.preventDefault();
                    this.selectedIndex = Math.min(this.selectedIndex + 1, this.suggestions.length - 1);
                    break;
                case 'ArrowUp':
                    event.preventDefault();
                    this.selectedIndex = Math.max(this.selectedIndex - 1, -1);
                    break;
                case 'Enter':
                    event.preventDefault();
                    if (this.selectedIndex >= 0) {
                        this.selectSuggestion(this.suggestions[this.selectedIndex]);
                    }
                    break;
                case 'Escape':
                    this.showSuggestions = false;
                    this.selectedIndex = -1;
                    break;
            }
        },
        selectSuggestion(suggestion) {
            this.inputValue = suggestion.formatted_address;
            this.$emit('input', suggestion.formatted_address);
            this.showSuggestions = false;
            this.selectedIndex = -1;
            
            // Récupérer les détails de l'adresse
            if (this.resolveDetails) this.getPlaceDetails(suggestion.place_id);
        },
        performSearch(query) {
            if (!query || query.length < 3) {
                this.suggestions = [];
                this.showSuggestions = false;
                return;
            }

            // Debounce the search
            if (this.debounceTimer) {
                clearTimeout(this.debounceTimer);
            }

            this.debounceTimer = setTimeout(() => {
                this.searchPlaces(query);
            }, 300);
        },
        searchPlaces(query) {
            
            if (!this.autocompleteService) {
                this.initializeGoogleServices();
                if (!this.autocompleteService) {
                    console.error('Google Places API not loaded after initialization attempt');
                    this.loading = false;
                    // Try to load Google Maps script manually as last resort
                    this.loadGoogleMapsManually();
                    return;
                }
            }

            this.loading = true;

            const request = {
                input: query,
                types: ['address'],
                componentRestrictions: { country: ['fr', 'be', 'ch', 'ca', 'us', 'gb', 'de', 'es', 'it'] }
            };


            this.autocompleteService.getPlacePredictions(request, (predictions, status) => {
                this.loading = false;
                
                if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
                    this.suggestions = predictions.map(prediction => ({
                        place_id: prediction.place_id,
                        formatted_address: prediction.description,
                        types: prediction.types,
                        prediction: prediction
                    }));
                    
                    // Afficher les suggestions si le champ est focusé
                    if (this.isFocused) {
                        this.showSuggestions = true;
                    }
                } else if (status === window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
                    this.suggestions = [];
                    this.showSuggestions = false;
                } else {
                    console.error('Google Places API error:', status);
                    this.suggestions = [];
                    this.showSuggestions = false;
                }
            });
        },
        loadGoogleMapsManually() {
            
            if (window.google && window.google.maps) {
                this.initializeGoogleServices();
                return;
            }

            const script = document.createElement('script');
            const apiKey = process.env.VUE_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyAD5VT2D1p-tzNXavpHO-rDohvcug_W_Lo';
            script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&region=FR&language=fr`;
            script.async = true;
            script.defer = true;
            
            script.onload = () => {
                this.initializeGoogleServices();
            };
            
            script.onerror = (error) => {
                console.error('Failed to load Google Maps script manually:', error);
            };
            
            document.head.appendChild(script);
        },
        getPlaceDetails(placeId) {
            if (!this.placesService) {
                this.initializeGoogleServices();
                if (!this.placesService) {
                    console.warn('Google Places API not loaded');
                    return;
                }
            }

            const request = {
                placeId: placeId,
                fields: ['address_components', 'formatted_address', 'geometry']
            };

            this.placesService.getDetails(request, (place, status) => {
                if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
                    const addressData = this.parseAddressComponents(place);
                    this.$emit('address-selected', addressData);
                }
            });
        },
        parseAddressComponents(place) {
            const components = place.address_components || [];
            const addressData = {
                formatted_address: place.formatted_address,
                line1: '',
                line2: '',
                postal_code: '',
                city: '',
                country: '',
                geometry: place.geometry
            };

            let streetNumber = '';
            let route = '';
            let subpremise = '';
            let sublocality = '';

            components.forEach(component => {
                const types = component.types;
                if (types.includes('street_number')) {
                    streetNumber = component.long_name;
                } else if (types.includes('route')) {
                    route = component.long_name;
                } else if (types.includes('subpremise')) {
                    subpremise = component.long_name;
                } else if (types.includes('sublocality')) {
                    sublocality = component.long_name;
                } else if (types.includes('postal_code')) {
                    addressData.postal_code = component.long_name;
                } else if (types.includes('locality')) {
                    addressData.city = component.long_name;
                } else if (types.includes('country')) {
                    addressData.country = component.short_name;
                }
            });

            // Construire line1 avec numéro et rue
            if (streetNumber && route) {
                addressData.line1 = `${streetNumber} ${route}`;
            } else if (route) {
                addressData.line1 = route;
            } else if (streetNumber) {
                addressData.line1 = streetNumber;
            }

            // Construire line2 avec subpremise ou sublocality
            if (subpremise) {
                addressData.line2 = subpremise;
            } else if (sublocality) {
                addressData.line2 = sublocality;
            }

            this.inputValue = addressData.line1;
            return addressData;
        },
        getAddressType(types) {
            const typeMap = {
                'street_address': this.$translate('address_types.street_address'),
                'route': this.$translate('address_types.route'),
                'intersection': this.$translate('address_types.intersection'),
                'political': this.$translate('address_types.political'),
                'country': this.$translate('address_types.country'),
                'administrative_area_level_1': this.$translate('address_types.administrative_area_level_1'),
                'administrative_area_level_2': this.$translate('address_types.administrative_area_level_2'),
                'locality': this.$translate('address_types.locality'),
                'sublocality': this.$translate('address_types.sublocality'),
                'neighborhood': this.$translate('address_types.neighborhood'),
                'premise': this.$translate('address_types.premise'),
                'subpremise': this.$translate('address_types.subpremise'),
                'postal_code': this.$translate('address_types.postal_code')
            };

            const mainType = types.find(type => typeMap[type]);
            return mainType ? typeMap[mainType] : types[0] || '';
        }
    },
    watch: {
        value(newValue) {
            if (newValue !== this.selectedAddress) {
                this.selectedAddress = newValue;
                this.searchInput = newValue;
                this.inputValue = newValue;
            }
        }
    },
    i18n: {
        messages: {
            en: {
                address_types: {
                    street_address: 'Street Address',
                    route: 'Street',
                    intersection: 'Intersection',
                    political: 'Political',
                    country: 'Country',
                    administrative_area_level_1: 'State/Province',
                    administrative_area_level_2: 'County',
                    locality: 'City',
                    sublocality: 'District',
                    neighborhood: 'Neighborhood',
                    premise: 'Building',
                    subpremise: 'Apartment/Suite',
                    postal_code: 'Postal Code'
                }
            },
            fr: {
                address_types: {
                    street_address: 'Adresse',
                    route: 'Rue',
                    intersection: 'Intersection',
                    political: 'Politique',
                    country: 'Pays',
                    administrative_area_level_1: 'Région/Province',
                    administrative_area_level_2: 'Département',
                    locality: 'Ville',
                    sublocality: 'Arrondissement',
                    neighborhood: 'Quartier',
                    premise: 'Bâtiment',
                    subpremise: 'Appartement/Suite',
                    postal_code: 'Code Postal'
                }
            }
        }
    }
};
</script>

<style scoped>
.suggestions-dropdown {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    right: 0;
    z-index: 1000;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    box-shadow: 0 12px 28px rgba(15, 23, 42, .12), 0 2px 6px rgba(15, 23, 42, .04);
    max-height: 340px;
    overflow-y: auto;
    overscroll-behavior: contain;
}
.address-suggestions-list { padding: 6px; border-radius: inherit; }
.address-suggestion { gap: 10px; padding: 10px; min-height: 60px; border-radius: 8px; cursor: pointer; }
.address-suggestion:hover, .address-suggestion.v-list-item--active { background: #f3f4ff; }
.address-suggestion::before { display: none; }
.address-suggestion-icon { display: flex; align-items: center; justify-content: center; flex: 0 0 32px; height: 32px; background: #f1f5f9; border-radius: 9px; }
.address-suggestion-icon .v-icon { color: #64748b; }
.address-suggestion.v-list-item--active .address-suggestion-icon { background: #e0e7ff; }
.address-suggestion.v-list-item--active .v-icon { color: #4f46e5; }
.address-suggestion .v-list-item__content { padding: 0; min-width: 0; }
.address-suggestion .address-suggestion-title { white-space: normal; overflow-wrap: anywhere; font-size: 13px; line-height: 19px; font-weight: 500; color: #1e293b; }
.address-suggestion .address-suggestion-subtitle { white-space: normal; overflow-wrap: anywhere; margin-top: 3px; font-size: 12px; line-height: 17px; font-weight: 400; color: #64748b; }

.v-autocomplete .v-list-item__title {
    font-size: 0.875rem;
    line-height: 1.25rem;
}

.v-autocomplete .v-list-item__subtitle {
    font-size: 0.75rem;
    opacity: 0.7;
}

/* Tailwind-style when inputStyle === 'tailwind' (e.g. from AddressForm). Match native rounded = 0.25rem. */
.input-style-tailwind ::v-deep .v-text-field {
    border-radius: 0.25rem !important;
    overflow: hidden !important;
}
.input-style-tailwind ::v-deep .v-text-field .v-input__slot {
    background-color: #f8fafc !important;
    border: 1px solid #f5f6f9 !important;
    border-radius: 0.25rem !important;
    min-height: 40px !important;
    padding-left: 12px !important;
    padding-right: 12px !important;
}
.input-style-tailwind ::v-deep .v-text-field fieldset {
    border-radius: 0.25rem !important;
}
.input-style-tailwind ::v-deep .v-text-field.v-input--is-focused .v-input__slot {
    border-color: #3b82f6 !important;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3) !important;
}
</style>
